import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { Loader2, Save, Settings } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

function GSTConfig() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    theater_gst_rate: 18.0,
    cake_gst_rate: 5.0,
    decoration_gst_rate: 18.0,
    food_gst_rate: 5.0,
    miscellaneous_gst_rate: 18.0
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/gst-config`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setConfig(data);
      }
    } catch (error) {
      console.error('Error fetching GST config:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch GST configuration',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/gst-config`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(config)
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'GST configuration updated successfully'
        });
      } else {
        throw new Error('Failed to update GST configuration');
      }
    } catch (error) {
      console.error('Error saving GST config:', error);
      toast({
        title: 'Error',
        description: 'Failed to save GST configuration',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setConfig(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0
    }));
  };

  if (user?.role !== 'admin') {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">Access denied. Admin only.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
          <Settings className="h-8 w-8 text-blue-600" />
          GST Configuration
        </h1>
        <p className="text-gray-500 mt-2">Configure GST rates for different item categories</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>GST Rate Settings (in %)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="theater_gst_rate">Theater Booking GST Rate (%)</Label>
              <Input
                id="theater_gst_rate"
                type="number"
                step="0.01"
                value={config.theater_gst_rate}
                onChange={(e) => handleChange('theater_gst_rate', e.target.value)}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">Current: {config.theater_gst_rate}%</p>
            </div>

            <div>
              <Label htmlFor="cake_gst_rate">Cake GST Rate (%)</Label>
              <Input
                id="cake_gst_rate"
                type="number"
                step="0.01"
                value={config.cake_gst_rate}
                onChange={(e) => handleChange('cake_gst_rate', e.target.value)}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">Current: {config.cake_gst_rate}%</p>
            </div>

            <div>
              <Label htmlFor="decoration_gst_rate">Decoration GST Rate (%)</Label>
              <Input
                id="decoration_gst_rate"
                type="number"
                step="0.01"
                value={config.decoration_gst_rate}
                onChange={(e) => handleChange('decoration_gst_rate', e.target.value)}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">Current: {config.decoration_gst_rate}%</p>
            </div>

            <div>
              <Label htmlFor="food_gst_rate">Food & Beverages GST Rate (%)</Label>
              <Input
                id="food_gst_rate"
                type="number"
                step="0.01"
                value={config.food_gst_rate}
                onChange={(e) => handleChange('food_gst_rate', e.target.value)}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">Current: {config.food_gst_rate}%</p>
            </div>

            <div>
              <Label htmlFor="miscellaneous_gst_rate">Miscellaneous Items GST Rate (%)</Label>
              <Input
                id="miscellaneous_gst_rate"
                type="number"
                step="0.01"
                value={config.miscellaneous_gst_rate}
                onChange={(e) => handleChange('miscellaneous_gst_rate', e.target.value)}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">Current: {config.miscellaneous_gst_rate}%</p>
            </div>
          </div>

          <div className="pt-4 border-t">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="w-full md:w-auto"
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Configuration
                </>
              )}
            </Button>
          </div>

          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <h3 className="font-semibold text-blue-900 mb-2">Important Notes:</h3>
            <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
              <li>GST rates are applied when generating GST bills</li>
              <li>For intrastate sales: CGST and SGST (each 50% of total GST)</li>
              <li>For interstate sales: IGST (full GST rate)</li>
              <li>Common rates: Services - 18%, Food items - 5%</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default GSTConfig;
